import React, { useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import { styled } from '@mui/material/styles';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: "#F5F5F5",
}));

function MUITableComponent({ 
  tableData, 
  columns, 
  hasActions, 
  setLoad, 
  handleCellClick = () => { },
  rowsPerPageOptions = [5, 10, 25],
  searchTerm = "",
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [orderBy, setOrderBy] = React.useState("");
  const [order, setOrder] = React.useState("asc");
  
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = React.useMemo(() => {
    if (orderBy) {
      return [...tableData].sort((a, b) => {
        const column = columns.find((col) => col.id === orderBy);
        if (column && column.sortFunction) {
          return order === "asc"
            ? column.sortFunction(a, b)
            : column.sortFunction(b, a);
        }

        // Default sorting logic
        if (a[orderBy] < b[orderBy]) return order === "asc" ? -1 : 1;
        if (a[orderBy] > b[orderBy]) return order === "asc" ? 1 : -1;
        return 0;
      });
    }
    return tableData;
  }, [tableData, order, orderBy, columns]);
  
  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper variant="outlined">
      <TableContainer>
        <Table size="small" sx={{ minWidth: 650 }} aria-label="users table">
          <TableHead>
            <StyledTableRow>
              {columns.map((column) => (
                <TableCell key={column.id} style={{ width: column.width }}>
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => (
              <TableRow 
                key={index}
                sx={{
                  backgroundColor: searchTerm && 
                    Object.values(row).some(value => 
                      String(value).toLowerCase().includes(searchTerm.toLowerCase())
                    ) ? '#fff3e0' : 'inherit'
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    onClick={column.clickable ? () => handleCellClick(row) : null}
                    sx={{ ...column.styling, cursor: column.clickable ? "pointer" : "default" }}
                  >
                    {column.render ? column.render(row) : row[column.id]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={tableData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        showFirstButton
        showLastButton
      />
    </Paper>
  );
}

export default MUITableComponent;
